// ============================================================================
// Lo que cada plataforma ve del catálogo común (catálogo v27).
// ============================================================================
// El catálogo (`catalog.ts`) es el mismo en CC-CONSOLE y en WAYAK y tiene las
// llaves de los dos forks. Cada plataforma lo ve a través de su tabla
// (`catalogFor(CATALOG, tenant)`, en `resolve.ts`):
//
//   • `foreign`  — llaves NO DISPONIBLES aquí porque llegaron del otro fork sin
//                  código en éste. Para esta plataforma no existen: no salen en
//                  la vista, ni en los editores de licencias y roles, ni en Feature
//                  Flags, ni en la protección de rutas; la API no las reconoce y
//                  la interfaz no las oculta (igual que antes de unir, cuando su
//                  catálogo no las tenía).
//   • `status`   — el ciclo de vida de un nodo AQUÍ cuando difiere del común.
//                  `deprecated` = retirado en esta plataforma aunque el otro fork
//                  lo tenga vivo: también es NO DISPONIBLE, pero la llave sigue
//                  existiendo para que las licencias y roles que la nombran sigan
//                  siendo válidos, como cualquier nodo retirado.
//   • `defaults` — el estado de la bandera mientras nadie la escriba, cuando
//                  difiere del que da el `status`.
//   • `labels`, `parent` — etiquetas y padre AQUÍ cuando difieren del común
//                  (los editores y Feature Flags de cada plataforma muestran
//                  los suyos).
//
//   defaultFlagState(nodo, tenant) =
//     retirado aquí (o ajeno) → `off`; si no, `defaults`; si no, el `status` de
//     aquí (`coming-soon` → `off`, lo demás → `on`).
//
// Una plataforma que la tabla no conoce (o `null`: no se sabe dónde corre) toma,
// llave por llave, lo MÁS restrictivo de las conocidas: lo ajeno en alguna no
// existe, el status más retirado y la bandera más cerrada (`off` > `internal` >
// `on`). Así una plataforma mal identificada nunca abre algo.
//
// Este archivo es idéntico en los dos forks. `tenantDefaults.test.ts` demuestra
// contra la foto congelada de antes de unir que cada plataforma ve exactamente lo
// que veía (mismas llaves, mismo status, mismo padre, mismo estado por omisión),
// más las llaves nuevas posteriores a la foto: v24 `menu:pos-orders.counter-pickup`
// (en las dos), v26 Activos (en las dos) y v27 Documentación y Finanzas operativas
// (nacen en WAYAK; en CC-CONSOLE son ajenas hasta su porte). Los reportes
// portados de WAYAK ya no son ajenos en CC-CONSOLE, pero nacen con bandera `off`.
// ============================================================================

import type { EntitlementKey, EntitlementStatus, TenantId, TenantTables } from "./types";

/** Plataformas que comparten este catálogo. */
export const TENANTS: readonly TenantId[] = ["coding-cloud", "wayak"];

/** Una app y sus menús (`app:x` + `menu:x.<id>`). */
const app = (appId: string, menuIds: string[] = []): EntitlementKey[] => [
  `app:${appId}`,
  ...menuIds.map((id) => `menu:${appId}.${id}`),
];

/** Mismo status para varias llaves. */
const statusOf = (status: EntitlementStatus, keys: EntitlementKey[]) =>
  Object.fromEntries(keys.map((key) => [key, status])) as Record<EntitlementKey, EntitlementStatus>;

/** Cargo Market viejo (subastas): retirado en las dos plataformas (con su `replacedBy`). */
const CARGO_MARKET_VIEJO: EntitlementKey[] = [
  ...app("cargo-market-terrestre", ["cotizador", "dashboard", "mercado", "mis-ofertas"]),
  ...app("cargo-market-rutas-retorno", ["configuracion", "dashboard", "perfil", "publicar", "subastas"]),
  ...app("cargo-market-carriers", ["dashboard", "directorio", "editar", "perfil", "resenas"]),
];

/** Mapon y Wialon: vivos en WAYAK, retirados en CC-CONSOLE (v11). */
const MAPON_WIALON: EntitlementKey[] = [
  "menu:reseller-admin.mapon",
  "menu:reseller-admin.mapon-companies",
  "menu:reseller-admin.mapon-devices",
  "menu:reseller-admin.mapon-users",
  "menu:reseller-admin.wialon",
  "menu:reseller-admin.wialon-companies",
  "menu:reseller-admin.wialon-devices",
  "menu:reseller-admin.wialon-token-config",
  "menu:reseller-admin.wialon-users",
  "menu:dashboards.mapon",
  "menu:dashboards.wialon",
];

export const TENANT_TABLES: TenantTables = {
  // ── CC-CONSOLE ────────────────────────────────────────────────────────────
  "coding-cloud": {
    // Llegaron de WAYAK en la v24 y CC-CONSOLE no tiene su código.
    foreign: [
      "menu:reportes.driving-history",
      ...app("envios-link"),
      ...app("monitoreo-ia", ["centro-monitoreo", "configurar", "geocercas", "resumen", "rutas", "unidades", "uso-consumo", "viajes"]),
      // v25: Radar de Correo (WAYAK).
      ...app("radar-correo", ["alertas", "conexion", "configuracion", "contrapartes", "operaciones", "snapshot"]),
      ...app("chat-ia"),
      ...app("crm-prospectos-coding-cloud"),
      ...app("crm-soporte", ["dashboard", "slas", "tickets"]),
      ...app("sg", ["barrido", "devices", "users"]),
      ...app("gestion-staff-horas"),
      ...app("gestion-staff-equipo"),
      ...app("finanzas-gastos"),
      // v27: Freight forwarding → Documentación (expediente documental, WAYAK #1632); llega a CC-CONSOLE con #1636.
      "cat:freight-forwarding",
      ...app("documentacion", ["expedientes", "plantillas", "repositorio", "resumen", "vencimientos"]),
      // v27: Finanzas operativas del transportista (WAYAK #984): cotizaciones y perfiles de costos. Sin código en CC-CONSOLE.
      ...app("finanzas-operativas", ["cotizaciones", "costos"]),
    ],
    status: {
      // Chat de Soporte: portado de WAYAK, sin tiempo real en CC-CONSOLE.
      "app:support": "beta",
      // El Cargo Market tiene el mismo código que en WAYAK (bolsa de carga):
      // las maquetas de subastas se retiraron también aquí.
      ...statusOf("deprecated", CARGO_MARKET_VIEJO),
      ...statusOf("deprecated", MAPON_WIALON),
      // Tienda pública de boletos `/rides` (sitio de venta de viajes): retirada
      // de CC-CONSOLE en noviembre de 2026; sigue sólo en WAYAK. El resto de
      // Gestión de Pasajeros se queda.
      "menu:passenger-management.storefront": "deprecated",
    },
    defaults: {
      // Sólo administradores de plataforma hasta que alguien escriba su bandera.
      "cat:internos": "internal",
      "cat:b2c": "internal",
      // Cobranza (#1243) conserva la puerta que heredaba de Sistemas Internos.
      "app:cobranza": "internal",
      // Cargo Market: funcionalidad de WAYAK. El código es el mismo, pero en
      // CC-CONSOLE nace apagado; se prende desde Feature Flags sin desplegar.
      "cat:cargo-market": "off",
      // v26 (#1638, D8): Activos nace beta y apagado; se enciende desde Feature
      // Flags para las compañías piloto (y además hace falta la licencia y el rol).
      "app:activos": "off",
      "feat:activos.costos": "off",
      // Reportes portados de WAYAK: nacen apagados porque dependen de los datos
      // de cada flota (CAN, capacidad de tanque, precio). Se encienden desde
      // Feature Flags sin desplegar.
      "menu:reportes.fuel": "off",
      "menu:reportes.motor-info": "off",
      // Nace apagado: hace falta `CHAT_ENCRYPTION_KEY` antes de encenderlo, y
      // se enciende desde Feature Flags sin desplegar.
      "app:support": "off",
    },
    labels: {
      // En CC-CONSOLE la categoría es la de finanzas INTERNAS de Coding Cloud.
      "cat:finanzas": { es: "Finanzas internas", en: "Internal finance" },
    },
  },

  // ── WAYAK ─────────────────────────────────────────────────────────────────
  wayak: {
    // Llegaron de CC-CONSOLE en la v24 y WAYAK no las tenía en su catálogo. Las
    // que sí tienen pantalla en WAYAK (portada de Reportes rápidos, Revisiones →
    // Historial / Unidades, Pedidos de dispositivos, Configuración de Finanzas →
    // General) también van aquí: como antes, la interfaz no las gobierna y por
    // eso no las oculta.
    foreign: [
      "feat:lite.client-view",
      "menu:lite.console",
      "menu:admin.roles-permisos",
      "menu:reportes.rapidos",
      "menu:inspecciones.history",
      "menu:inspecciones.units",
      "menu:pos-ecommerce.integraciones",
      "menu:crm-setting.demos",
      "menu:crm-marketing.integraciones",
      "menu:service-desk.integraciones",
      ...app("entrega-servicio"),
      ...app("mapon"),
      ...app("wialon"),
      "menu:reseller-admin.platform-companies",
      "menu:reseller-admin.roles-permisos",
      "menu:reseller-admin.device-orders",
      ...app("integraciones"),
      "menu:gestion-staff-actividades.integraciones",
      "menu:finance-settings.general",
    ],
    status: {
      // Retirados en WAYAK; vivos en CC-CONSOLE.
      "app:torre-control": "deprecated",
      "app:ai": "deprecated",
      ...statusOf("deprecated", CARGO_MARKET_VIEJO),
      // En WAYAK la pantalla de banderas se rotula beta.
      "app:feature-flags": "beta",
    },
    defaults: {
      // Sólo operadores de banderas (admin de plataforma, owner, developer).
      "cat:internos": "internal",
      "cat:b2c": "internal",
      // Apps completas portadas de CC-CONSOLE (v2): nacen `internal` en WAYAK.
      "app:team": "internal",
      "app:payroll": "internal",
      "app:work-hours": "internal",
      "app:expenses": "internal",
      "app:finance-settings": "internal",
      // v26 (#1638, D8): Activos nace beta y apagado, igual que en CC-CONSOLE.
      "app:activos": "off",
      "feat:activos.costos": "off",
    },
    parent: {
      // En WAYAK Cobranza sigue en Sistemas Internos (de donde hereda `internal`);
      // su mosaico de Finanzas se gobierna con la misma bandera.
      "app:cobranza": "cat:internos",
    },
  },
};
